import { useQuery } from "@tanstack/react-query";
import { toast } from "@/shared/hooks/use-toast";
import { mediaDownloadFilename, triggerDownload } from "@/shared/lib/download";
import { Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { MediaPreviewLightbox } from "@/entities/media-asset/ui/MediaPreviewLightbox";
import { videoService } from "../../api/videoService";

interface Props {
  /** 任务 id;null = 关闭态(不拉取)。 */
  taskId: number | null;
  taskName?: string;
  onClose: () => void;
  onEnterDetail: (taskId: number) => void;
  /**
   * OEM H1:模块未开通 / 停用 / 过期 → 只预览不进详情(详情是编辑 / 新建面);传入即把「进入详情」换成这句提示。
   */
  lockedNotice?: string;
}

/**
 * 视频列表「单击预览」:列表卡不含可播放 URL(presigned 仅详情端点),点 SUCCESS 卡时按需 getTask
 * 拿 FINAL 成片 videoUrl 喂通用 MediaPreviewLightbox(video 态)。加载中/无成片自渲占位 + 进入详情。
 */
export function VideoPreviewDialog({
  taskId,
  taskName,
  onClose,
  onEnterDetail,
  lockedNotice,
}: Props) {
  const open = taskId !== null;
  const q = useQuery({
    queryKey: ["video", "task", taskId],
    queryFn: () => videoService.getTask(taskId as number),
    enabled: open,
  });
  const assets = q.data?.assets ?? [];
  const finalAsset =
    assets.find((a) => a.kind === "FINAL" && a.status === "SUCCESS") ??
    assets.find((a) => a.status === "SUCCESS");
  const url = finalAsset?.videoUrl;

  /**
   * ★1004 T9:海报预览有「下载」,视频预览只有「进入详情」—— 想拿成片得多跳一页。
   * 与详情页「下载成片」同一条路:问后端要带 Content-Disposition 的签名链接,文件名取任务名。
   * 未开通 / 停用的线同样可下载(OEM H1:看、下载、删可以,新建不行)。
   */
  const download = async () => {
    if (!finalAsset) return;
    try {
      const { url: dl } = await videoService.getDownloadUrl(finalAsset.id);
      triggerDownload(
        dl,
        mediaDownloadFilename(taskName ?? q.data?.name, `video-${finalAsset.id}`, "mp4")
      );
    } catch {
      toast({ title: "下载失败", description: "请稍后重试,或进入详情页下载", variant: "destructive" });
    }
  };

  // 加载中或无可播放成片:自渲占位 Dialog(MediaPreviewLightbox items 为空会返回 null,不会显示)。
  if (open && !url) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-4xl gap-3 p-4">
          <DialogTitle className="sr-only">视频预览</DialogTitle>
          <div className="flex min-h-[40vh] items-center justify-center rounded-lg bg-black/40">
            {q.isLoading ? (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            ) : (
              <span className="text-sm text-muted-foreground">暂无可预览的成片</span>
            )}
          </div>
          <div className="flex justify-end">
            {lockedNotice ? (
              <span className="text-xs text-muted-foreground" data-testid="module-locked-notice">
                {lockedNotice}
              </span>
            ) : (
              <Button size="sm" onClick={() => taskId !== null && onEnterDetail(taskId)}>
                进入详情
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <MediaPreviewLightbox
      open={open}
      onOpenChange={(o) => !o && onClose()}
      items={url ? [{ url, kind: "video", title: taskName }] : []}
      index={0}
      onIndexChange={() => {}}
      onDownload={finalAsset ? () => void download() : undefined}
      onEnterDetail={lockedNotice ? undefined : () => taskId !== null && onEnterDetail(taskId)}
      detailLabel="进入详情"
    />
  );
}
